import { Router } from "express";
import { z } from "zod";
import { and, asc, count, desc, eq, isNull, sql, type SQL } from "drizzle-orm";
import { db } from "../../db/index";
import { letters, letterTemplates } from "../../db/schema/letter";
import { users } from "../../db/schema/identity";
import { employeeDetails } from "../../db/schema/employee";
import { teams, designations } from "../../db/schema/hr";
import { companies } from "../../db/schema/tenancy";
import { alias } from "drizzle-orm/mysql-core";
import { requireAuth, requirePermission, scopeCondition, SCOPE } from "../../rbac/index";

/**
 * Letters (legacy V2 LettersController L24–301). Compose a letter from a template, with ##VAR##
 * merge fields resolved against the recipient employee (+ signatory = current user + company).
 * Company-scoped; writes gate ADD/EDIT/DELETE=ALL. PDF is browser print-to-PDF on the View screen
 * (best fit for cPanel/LiteSpeed Node — no headless-Chrome dependency); a server renderer can be
 * added later behind the same View. Templates managed inline. Soft-delete via deleted_at.
 */
export const letterRouter = Router();
letterRouter.use(requireAuth);
const onlyAll = (perm: string) => (req: import("express").Request, res: import("express").Response, next: import("express").NextFunction) =>
  req.scope === SCOPE.ALL ? next() : res.status(403).json({ error: "Forbidden", permission: perm });

/** Friendly label → ##TOKEN## (the editor's merge-field palette; legacy mergeFields L138–157). */
const MERGE_FIELDS: { label: string; token: string }[] = [
  { label: "Employee name", token: "##EMPLOYEE_NAME##" },
  { label: "Employee ID", token: "##EMPLOYEE_ID##" },
  { label: "Designation", token: "##EMPLOYEE_DESIGNATION##" },
  { label: "Department", token: "##EMPLOYEE_DEPARTMENT##" },
  { label: "Address", token: "##EMPLOYEE_ADDRESS##" },
  { label: "Joining date", token: "##EMPLOYEE_JOINING_DATE##" },
  { label: "Exit date", token: "##EMPLOYEE_EXIT_DATE##" },
  { label: "Probation end", token: "##EMPLOYEE_PROBATION_END_DATE##" },
  { label: "Notice start", token: "##EMPLOYEE_NOTICE_PERIOD_START_DATE##" },
  { label: "Notice end", token: "##EMPLOYEE_NOTICE_PERIOD_END_DATE##" },
  { label: "Date of birth", token: "##EMPLOYEE_DOB##" },
  { label: "Current date", token: "##CURRENT_DATE##" },
  { label: "Signatory", token: "##SIGNATORY##" },
  { label: "Signatory designation", token: "##SIGNATORY_DESIGNATION##" },
  { label: "Company name", token: "##COMPANY_NAME##" },
];

const ddmmyyyy = (v: unknown): string => {
  if (!v) return "";
  const d = new Date(v as string);
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
};

/** Build the token→value map for a recipient (+ the signatory = the current user) and company. */
async function tokenValues(companyId: number, recipientId: number | null, freeName: string | null, signatoryId: number): Promise<Record<string, string>> {
  const [company] = await db.select({ name: companies.companyName }).from(companies).where(eq(companies.id, companyId)).limit(1);
  const base: Record<string, string> = {
    "##CURRENT_DATE##": ddmmyyyy(new Date()),
    "##COMPANY_NAME##": company?.name ?? "",
  };
  // signatory = current user
  const sig = await loadPerson(signatoryId);
  base["##SIGNATORY##"] = sig?.name ?? "";
  base["##SIGNATORY_DESIGNATION##"] = sig?.designation ?? "";
  if (recipientId == null) {
    base["##EMPLOYEE_NAME##"] = freeName ?? ""; // free-text letter — only the name resolves (legacy)
    return base;
  }
  const p = await loadPerson(recipientId);
  return {
    ...base,
    "##EMPLOYEE_NAME##": p?.name ?? "",
    "##EMPLOYEE_ID##": p?.code ?? "",
    "##EMPLOYEE_DESIGNATION##": p?.designation ?? "",
    "##EMPLOYEE_DEPARTMENT##": p?.department ?? "",
    "##EMPLOYEE_ADDRESS##": p?.address ?? "",
    "##EMPLOYEE_JOINING_DATE##": ddmmyyyy(p?.joining),
    "##EMPLOYEE_EXIT_DATE##": ddmmyyyy(p?.lastDate),
    "##EMPLOYEE_PROBATION_END_DATE##": ddmmyyyy(p?.probation),
    "##EMPLOYEE_NOTICE_PERIOD_START_DATE##": ddmmyyyy(p?.noticeStart),
    "##EMPLOYEE_NOTICE_PERIOD_END_DATE##": ddmmyyyy(p?.noticeEnd),
    "##EMPLOYEE_DOB##": ddmmyyyy(p?.dob),
  };
}
async function loadPerson(userId: number) {
  const [r] = await db
    .select({
      name: users.name, code: employeeDetails.employeeId, address: employeeDetails.address,
      joining: employeeDetails.joiningDate, lastDate: employeeDetails.lastDate, probation: employeeDetails.probationEndDate,
      noticeStart: employeeDetails.noticePeriodStartDate, noticeEnd: employeeDetails.noticePeriodEndDate, dob: employeeDetails.dateOfBirth,
      department: teams.teamName, designation: designations.name,
    })
    .from(users)
    .leftJoin(employeeDetails, eq(employeeDetails.userId, users.id))
    .leftJoin(teams, eq(teams.id, employeeDetails.departmentId))
    .leftJoin(designations, eq(designations.id, employeeDetails.designationId))
    .where(eq(users.id, userId))
    .limit(1);
  return r;
}
function resolve(body: string, map: Record<string, string>): string {
  let out = body;
  for (const [tok, val] of Object.entries(map)) out = out.split(tok).join(val ?? "");
  return out;
}

/** GET /api/letters — list + KPIs (legacy L237–301). */
letterRouter.get("/letters", requirePermission("view_letter"), async (req, res) => {
  const companyId = req.auth!.companyId;
  const templateId = Number(req.query.template_id) || 0;
  const search = (req.query.search as string | undefined)?.trim() ?? "";
  const creator = alias(users, "creator");
  const conds: SQL[] = [eq(letters.companyId, companyId), isNull(letters.deletedAt)];
  if (templateId) conds.push(eq(letters.templateId, templateId));
  if (search) conds.push(sql`(${users.name} like ${`%${search}%`} or ${letters.name} like ${`%${search}%`})`);
  // Scope filter: Owned → letters about me (user_id=me), Added → ones I created, All → company.
  const sc = scopeCondition(req.scope as 1 | 2 | 3 | 4 | 5, req.auth!.userId, { addedBy: letters.creatorId, owner: letters.userId });
  if (sc === "DENY") conds.push(sql`1 = 0`);
  else if (sc) conds.push(sc);

  const rows = await db
    .select({
      id: letters.id, name: letters.name, userId: letters.userId, recipient: users.name,
      template: letterTemplates.title, templateId: letters.templateId, creator: creator.name, createdAt: letters.createdAt,
    })
    .from(letters)
    .leftJoin(users, eq(users.id, letters.userId))
    .leftJoin(letterTemplates, eq(letterTemplates.id, letters.templateId))
    .leftJoin(creator, eq(creator.id, letters.creatorId))
    .where(and(...conds))
    .orderBy(desc(letters.id));

  const [tplCount] = await db.select({ c: count() }).from(letterTemplates).where(eq(letterTemplates.companyId, companyId));
  const mo = new Date().getMonth();
  const list = rows.map((r) => ({ id: r.id, subject: r.name || r.recipient || "Letter", recipient: r.recipient ?? r.name ?? "—", template: r.template ?? "—", templateId: r.templateId, creator: r.creator ?? "—", createdAt: r.createdAt }));
  res.json({
    rows: list,
    kpis: {
      total: list.length,
      thisMonth: rows.filter((r) => r.createdAt && new Date(r.createdAt as unknown as string).getMonth() === mo).length,
      recipients: new Set(rows.map((r) => r.userId).filter(Boolean)).size,
      templates: Number(tplCount?.c ?? 0),
    },
  });
});

/** GET /api/letters/options — templates + employees + merge-field palette (drawer). */
letterRouter.get("/letters/options", requirePermission("view_letter"), async (req, res) => {
  const companyId = req.auth!.companyId;
  const [tpls, emps] = await Promise.all([
    db.select({ id: letterTemplates.id, title: letterTemplates.title }).from(letterTemplates).where(eq(letterTemplates.companyId, companyId)).orderBy(asc(letterTemplates.title)),
    db.select({ id: users.id, name: users.name }).from(users).where(eq(users.companyId, companyId)).orderBy(asc(users.name)),
  ]);
  res.json({ templates: tpls, employees: emps, mergeFields: MERGE_FIELDS });
});

/** GET /api/letter-templates/:id/body — raw body for the editor on template-pick (legacy L70–75). */
letterRouter.get("/letter-templates/:id/body", requirePermission("view_letter"), async (req, res) => {
  const [t] = await db.select({ title: letterTemplates.title, description: letterTemplates.description }).from(letterTemplates).where(and(eq(letterTemplates.id, Number(req.params.id)), eq(letterTemplates.companyId, req.auth!.companyId))).limit(1);
  if (!t) return res.status(404).json({ error: "Not found" });
  res.json({ title: t.title, body: t.description ?? "" });
});

/** GET /api/letters/:id — resolved letter (legacy L46–67). */
letterRouter.get("/letters/:id", requirePermission("view_letter"), async (req, res) => {
  const id = Number(req.params.id);
  const [l] = await db.select().from(letters).where(and(eq(letters.id, id), eq(letters.companyId, req.auth!.companyId), isNull(letters.deletedAt))).limit(1);
  if (!l) return res.status(404).json({ error: "Not found" });
  const map = await tokenValues(req.auth!.companyId, l.userId, l.name, req.auth!.userId);
  res.json({
    id: l.id, name: l.name, userId: l.userId, templateId: l.templateId,
    description: l.description ?? "",
    html: resolve(l.description ?? "", map),
    margins: { top: l.top || "60", right: l.right || "60", left: l.left || "60", bottom: l.bottom || "60" },
  });
});

const letterBody = z.object({
  template_id: z.coerce.number().int().positive(),
  user_id: z.coerce.number().int().positive().nullable().optional(),
  name: z.string().nullable().optional(), // recipient picked → client sends name:null (not a string)
  description: z.string().default(""),
  top: z.string().default("60"), right: z.string().default("60"), left: z.string().default("60"), bottom: z.string().default("60"),
}).refine((b) => b.user_id || (b.name && b.name.trim()), { message: "Pick an employee or enter a name" });

letterRouter.post("/letters", requirePermission("add_letter"), onlyAll("add_letter"), async (req, res) => {
  const p = letterBody.safeParse(req.body);
  if (!p.success) return res.status(422).json({ error: "Invalid payload", details: p.error.flatten() });
  const b = p.data;
  const [r] = await db.insert(letters).values({
    companyId: req.auth!.companyId, templateId: b.template_id, userId: b.user_id ?? null, name: b.name ?? null,
    description: b.description, top: b.top, right: b.right, left: b.left, bottom: b.bottom, creatorId: req.auth!.userId,
  }).$returningId();
  res.status(201).json({ id: r!.id, message: "Letter created" });
});

letterRouter.post("/letters/:id", requirePermission("edit_letter"), onlyAll("edit_letter"), async (req, res) => {
  const id = Number(req.params.id);
  const p = letterBody.safeParse(req.body);
  if (!p.success) return res.status(422).json({ error: "Invalid payload", details: p.error.flatten() });
  const [exists] = await db.select({ id: letters.id }).from(letters).where(and(eq(letters.id, id), eq(letters.companyId, req.auth!.companyId))).limit(1);
  if (!exists) return res.status(404).json({ error: "Not found" });
  const b = p.data;
  await db.update(letters).set({ templateId: b.template_id, userId: b.user_id ?? null, name: b.name ?? null, description: b.description, top: b.top, right: b.right, left: b.left, bottom: b.bottom }).where(eq(letters.id, id));
  res.json({ id, message: "Letter updated" });
});

letterRouter.delete("/letters/:id", requirePermission("delete_letter"), onlyAll("delete_letter"), async (req, res) => {
  const id = Number(req.params.id);
  const [exists] = await db.select({ id: letters.id }).from(letters).where(and(eq(letters.id, id), eq(letters.companyId, req.auth!.companyId))).limit(1);
  if (!exists) return res.status(404).json({ error: "Not found" });
  await db.update(letters).set({ deletedAt: new Date() }).where(eq(letters.id, id)); // soft delete (legacy SoftDeletes)
  res.json({ id, message: "Letter deleted" });
});

/* ── Templates manager (legacy L165–236) ── */
letterRouter.get("/letter-templates", requirePermission("view_letter"), async (req, res) => {
  const rows = await db
    .select({ id: letterTemplates.id, title: letterTemplates.title, description: letterTemplates.description })
    .from(letterTemplates).where(eq(letterTemplates.companyId, req.auth!.companyId)).orderBy(asc(letterTemplates.title));
  res.json({ rows: rows.map((r) => ({ id: r.id, title: r.title, preview: (r.description ?? "").replace(/<[^>]+>/g, " ").slice(0, 120) })) });
});

const tplBody = z.object({ title: z.string().trim().min(1).max(255), description: z.string().min(1) });
letterRouter.post("/letter-templates", requirePermission("add_letter"), onlyAll("add_letter"), async (req, res) => {
  const p = tplBody.safeParse(req.body);
  if (!p.success) return res.status(422).json({ error: "Invalid payload", details: p.error.flatten() });
  const [r] = await db.insert(letterTemplates).values({ companyId: req.auth!.companyId, title: p.data.title, description: p.data.description }).$returningId();
  res.status(201).json({ id: r!.id, message: "Template created" });
});
letterRouter.put("/letter-templates/:id", requirePermission("edit_letter"), onlyAll("edit_letter"), async (req, res) => {
  const id = Number(req.params.id);
  const p = tplBody.safeParse(req.body);
  if (!p.success) return res.status(422).json({ error: "Invalid payload", details: p.error.flatten() });
  const [exists] = await db.select({ id: letterTemplates.id }).from(letterTemplates).where(and(eq(letterTemplates.id, id), eq(letterTemplates.companyId, req.auth!.companyId))).limit(1);
  if (!exists) return res.status(404).json({ error: "Not found" });
  await db.update(letterTemplates).set({ title: p.data.title, description: p.data.description }).where(eq(letterTemplates.id, id));
  res.json({ id, message: "Template updated" });
});
letterRouter.delete("/letter-templates/:id", requirePermission("delete_letter"), onlyAll("delete_letter"), async (req, res) => {
  const id = Number(req.params.id);
  const [used] = await db.select({ c: count() }).from(letters).where(and(eq(letters.templateId, id), isNull(letters.deletedAt)));
  if (Number(used?.c ?? 0) > 0) return res.status(422).json({ error: "Template is in use by existing letters" });
  await db.delete(letterTemplates).where(and(eq(letterTemplates.id, id), eq(letterTemplates.companyId, req.auth!.companyId)));
  res.json({ id, message: "Template deleted" });
});
